import { gsap, Power2 } from 'gsap';
import { Metrics } from '../core/Metrics';
import { Scroll } from '../scroll/Scroll';

export default class BG {
  static colors;
  static container = document.body;
  static actualPalette = null;
  static actualColor = null;
  static actualKey = null;
  static time = 0.2;
  static ease = Power2.easeOut;
  static keys;

  static _positions = {
    actual: 0,
    positionNext: Infinity,
    positionPrev: 0
  };
  static _changes = [];
  static _domPage;

  static init(__colors) {
    this.colors = __colors;
    this.keys = Object.keys(this.colors);
  }

  static initChanges(__container) {
    this.setupResize(__container);

    this._changes = [];
    this._domPage = __container ? __container : this._domPage;

    if (this._domPage) {
      this.setupChanges();
      this.setupActualPositions();
    }
  }

  static setupChanges() {
    if (!this._domPage) return;

    this._changes = [];
    this._changes.push({
      position: 0,
      palette: this._domPage.getAttribute('data-palette')
    });

    [...this._domPage.querySelectorAll('[data-palette]')].forEach((item) => {
      this._changes.push({
        position: item.offsetTop - Metrics.HEIGHT * 0.9,
        palette: item.getAttribute('data-palette')
      });
    });

    // Orden por posición (por si el DOM no está en orden de scroll)
    this._changes.sort((a, b) => a.position - b.position);
  }

  /** Índice de paleta según Scroll.y actual (Lenis: Y positiva hacia abajo). */
  static _indexForScroll(y = Scroll.y || 0) {
    let actual = 0;
    for (let i = 0; i < this._changes.length; i++) {
      if (y >= this._changes[i].position) actual = i;
      else break;
    }
    return actual;
  }

  static _applyIndex(actual, __time = 0.4) {
    if (!this._changes.length) return;

    const next = Math.min(this._changes.length - 1, actual + 1);

    this._positions.actual = actual;
    this._positions.positionPrev = this._changes[actual].position;
    this._positions.positionNext = actual >= this._changes.length - 1
      ? Infinity
      : this._changes[next].position;

    const key = this._changes[actual].palette;
    if (key) this.changePalette(key, null, __time);
  }

  static setupActualPositions() {
    if (this._changes.length === 0) return;
    this._applyIndex(this._indexForScroll(), 0);
  }

  static loop(__force = false) {
    if (this._changes.length <= 1) return;

    const y = Scroll.y || 0;
    const dir = Scroll.direction;

    const crossedDown = y > this._positions.positionNext && (dir === 1 || __force);
    const crossedUp = y < this._positions.positionPrev && (dir === -1 || __force);

    if (!crossedDown && !crossedUp && !__force) return;

    this._applyIndex(this._indexForScroll(y), __force ? 0 : 0.4);
  }

  static setupResize(__dom) {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.resize();
    });

    this.resizeObserver.observe(__dom);
  }

  static resize() {
    this._changes = [];
    this.setupChanges();
    this.setupActualPositions();
  }

  static nextPalette() {
    const actualIndex = this.keys.indexOf(this.actualKey);
    const newKey =
      actualIndex + 1 === this.keys.length
        ? this.keys[0]
        : this.keys[actualIndex + 1];
    this.changePalette(newKey);
  }

  static changePaletteDirect(__color, __call = null) {
    this.changePalette(__color, __call, 0);
  }

  static changePalette(
    __color,
    __call = null,
    __time = BG.time,
    __ease = null
  ) {
    if (!__color || !this.colors?.[__color]) {
      if (__call) __call();
      return;
    }

    this.actualKey = __color;
    const color = this.colors[__color];

    if (this.actualPalette !== color.str) {
      if (this.actualPalette !== null) {
        document.body.classList.remove(this.actualPalette);
      }

      this.actualPalette = color.str;
      document.body.classList.add(this.actualPalette);

      this.changeBG(color.css, __call, __time, __ease);
    } else if (__call) {
      __call();
    }
  }

  static changeBG(__color, __call = null, __time = BG.time, __ease = BG.ease) {
    if (this.actualColor === __color) {
      if (__call) __call();
      return;
    }

    this.actualColor = __color;

    if (__time === 0) {
      gsap.set(this.container, { backgroundColor: __color });
      if (__call) __call();
    } else {
      gsap.to(this.container, {
        backgroundColor: __color,
        duration: __time,
        ease: __ease,
        onComplete: () => {
          if (__call) __call();
        }
      });
    }
  }
}
